import { DateTime } from "luxon";
import { z } from "zod";

import {
  callEcwEmptyFormEndpoint,
  callEcwFormEndpoint,
  callEcwSoapEndpoint,
  EcwHttpMethod,
} from "@/src/lib/ehr/ecw/webecw/client";
import { ecwBool, ecwDate, ecwInt } from "@/src/lib/ehr/ecw/webecw/coerce";
import { EcwOperationFailedError, ecwResponsePreview } from "@/src/lib/ehr/ecw/webecw/errors";
import type { EcwSession } from "@/src/lib/ehr/ecw/webecw/session";
import { buildSoapEnvelope } from "@/src/lib/ehr/ecw/webecw/xml";

export const ActionStatus = z.enum(["1", "2", "3"]);
export type ActionStatus = z.infer<typeof ActionStatus>;

export const ActionPriority = z.enum(["1", "2", "3"]);
export type ActionPriority = z.infer<typeof ActionPriority>;

export interface ActionParams {
  mrn: string;
  subject: string;
  assignedToId: string;
  assignedToName: string;
  createdById: string;
  createdByName: string;
  facilityId: string;
  facilityName: string;
  note: string;
  timeZone: string;
  status?: ActionStatus;
  priority?: ActionPriority;
}

export interface UpdateActionParams extends ActionParams {
  actionId: string;
  notes: string;
}

const EcwActionDetail = z
  .object({
    Id: ecwInt,
    PatientId: z.string(),
    AssignedToId: z.string(),
    FacilityId: ecwInt,
    Subject: z.string(),
    ActionType: ecwInt,
    Status: ecwInt,
    CreationDate: z.string(),
    StartDate: z.string(),
    DueDate: z.string(),
    CompletionDate: z.string(),
    Notes: z.string(),
    Priority: ecwInt,
    IsRecurrent: ecwBool,
    UseAttachments: ecwInt,
    CreatedBy: z.string(),
    createdById: ecwInt,
    ModifiedById: ecwInt,
    Frequency: ecwInt,
    FrequencyType: z.string(),
    LastDue: z.string(),
    LastDone: z.string(),
    Context: z.string(),
    Trigger: z.string(),
    bEndRecurrence: ecwBool,
    PtAcoId: ecwInt,
    RecurrenceRange_Option: ecwInt,
    RecurrenceRange_EndAfter: ecwInt,
    RecurrenceRange_EndDate: z.string(),
    EncounterId: ecwInt,
    expirydate: ecwDate,
  })
  .strict();

export type EcwActionDetail = z.infer<typeof EcwActionDetail>;

const LoadActionReturn = z
  .object({
    item: EcwActionDetail,
  })
  .strict();

const ACTION_CDATA_FIELDS = ["Subject", "Notes", "CreatedBy"];

function ecwDateTimeStr(now: Date, timeZone: string): string {
  return DateTime.fromJSDate(now).setZone(timeZone).toFormat("yyyy-MM-dd HH:mm:ss");
}

export function formatNoteEntry(trUserName: string, noteText: string, timeZone: string): string {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
    timeZoneName: "short",
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const date = `${get("month")}/${get("day")}/${get("year")}`;
  const time = `${get("hour")}:${get("minute")}:${get("second")} ${get("dayPeriod")} ${get("timeZoneName")}`;

  return `${trUserName} ${date} ${time} > ${noteText}`;
}

export function appendNoteEntry(
  existingNotes: string | null | undefined,
  trUserName: string,
  noteText: string,
  timeZone: string
): string {
  const entry = formatNoteEntry(trUserName, noteText, timeZone);

  return existingNotes ? `${existingNotes}\n${entry}` : entry;
}

function buildActionFields(
  session: EcwSession,
  params: ActionParams,
  now: Date,
  id: string,
  notes: string,
  context: string,
  trigger: string
): Record<string, string> {
  const nowStr = ecwDateTimeStr(now, params.timeZone);

  return {
    EncounterId: "0",
    Id: id,
    PatientId: params.mrn,
    AssignedToId: params.assignedToId,
    FacilityId: params.facilityId,
    Subject: params.subject,
    ActionType: "0",
    Status: params.status ?? "1",
    CreationDate: nowStr,
    CompletionDate: nowStr,
    StartDate: nowStr,
    DueDate: nowStr,
    CreatedBy: params.createdByName,
    createdById: params.createdById,
    Priority: params.priority ?? "1",
    Notes: notes,
    Frequency: "0",
    FrequencyType: "Days",
    LastDue: nowStr,
    LastDone: nowStr,
    UseAttachments: "0",
    RecurrenceRange_Option: "0",
    RecurrenceRange_EndAfter: "0",
    RecurrenceRange_EndDate: nowStr,
    IsRecurrent: "0",
    ModifiedById: session.trUserId,
    Context: context,
    Trigger: trigger,
    bEndRecurrence: "false",
    PtAcoId: "0",
  };
}

function buildCreateActionLogsJSON(params: ActionParams & { notes: string }): string {
  return JSON.stringify({
    data: [
      ["Patient Name", "", ""],
      ["Created By", "", params.createdByName],
      ["Subject", "", params.subject],
      ["Assigned To", "", params.assignedToName],
      ["Facility", "", params.facilityName],
      ["Start Date", "", ""],
      ["Due Date", "", ""],
      ["Status", "", "Not Started"],
      ["Priority", "", "Normal"],
      ["Notes", "", params.notes],
    ],
  });
}

function buildUpdateActionLogsJSON(changed: Array<[string, string, string]>): string {
  return JSON.stringify({ data: changed });
}

async function postUpdateActions(
  session: EcwSession,
  soapXml: string,
  actionLogsJSON: string,
  attachmentLogsData?: string
): Promise<string> {
  const body: Record<string, string> = { FormData: soapXml, actionLogsJSON };

  if (attachmentLogsData !== undefined) {
    body.attachmentLogsData = attachmentLogsData;
  }

  const path = "/mobiledoc/jsp/catalog/xml/PtAddInfo/UpdateActions.jsp";

  return callEcwFormEndpoint(session, { method: EcwHttpMethod.POST, path, body }, (text) => {
    const id = text.trim();

    if (!id || !/^\d+$/.test(id)) {
      throw new EcwOperationFailedError(path, `expected numeric action id, got ${ecwResponsePreview(text)}`);
    }

    return id;
  });
}

async function postSetActionsAssignLog(session: EcwSession, actionId: string, assignedToId: string): Promise<void> {
  const soapXml = buildSoapEnvelope("Action", { AssignedToId: assignedToId, moduserid: session.trUserId });
  const body: Record<string, string> = { ActionId: actionId, FormData: soapXml };

  await callEcwEmptyFormEndpoint(session, {
    method: EcwHttpMethod.POST,
    path: "/mobiledoc/jsp/catalog/xml/PtAddInfo/SetActionsAssignLog.jsp",
    body,
  });
}

export async function createAction(session: EcwSession, params: ActionParams): Promise<string> {
  const now = new Date();
  const notes = formatNoteEntry(session.trUserName, params.note, params.timeZone);
  const soapXml = buildSoapEnvelope(
    "item",
    buildActionFields(session, params, now, "0", notes, "undefined", ""),
    ACTION_CDATA_FIELDS
  );
  const actionLogsJSON = buildCreateActionLogsJSON({ ...params, notes });
  const actionId = await postUpdateActions(session, soapXml, actionLogsJSON);

  await postSetActionsAssignLog(session, actionId, params.assignedToId);

  return actionId;
}

export async function updateAction(
  session: EcwSession,
  params: UpdateActionParams,
  changed: Array<[string, string, string]> = []
): Promise<void> {
  const now = new Date();
  const soapXml = buildSoapEnvelope(
    "item",
    buildActionFields(session, params, now, params.actionId, params.notes, "actionsByPatient", "cmdsave"),
    ACTION_CDATA_FIELDS
  );
  const actionLogsJSON = buildUpdateActionLogsJSON(changed);

  await postUpdateActions(session, soapXml, actionLogsJSON);
}

export async function attachDocumentToAction(
  session: EcwSession,
  actionId: string,
  documentNumericId: string
): Promise<void> {
  const attachSoap = buildSoapEnvelope("item", {
    AttachmentType: "Document",
    AttachmentId: documentNumericId,
  });
  const body: Record<string, string> = { ActionID: actionId, FormData: attachSoap };
  const path = "/mobiledoc/jsp/catalog/xml/PtAddInfo/UpdateActionsAttachments.jsp";

  await callEcwFormEndpoint(session, { method: EcwHttpMethod.POST, path, body }, (text) => {
    if (text.trim().toLowerCase() !== "success") {
      throw new EcwOperationFailedError(path, `unexpected response: ${ecwResponsePreview(text)}`);
    }
  });

  await postSetActionsAssignLog(session, actionId, session.trUserId);
}

export async function loadAction(session: EcwSession, actionId: string): Promise<EcwActionDetail> {
  const ret = await callEcwSoapEndpoint(
    session,
    {
      method: EcwHttpMethod.POST,
      path: "/mobiledoc/jsp/catalog/xml/PtAddInfo/LoadAction.jsp",
      query: { ActionId: actionId },
    },
    LoadActionReturn
  );

  return ret.item;
}
